import { Fragment, ReactElement } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import SubscriptionBirthNonResidentPage from 'shell/components/shared/research-notifications/reasearch-birth-non-resident/birth-non-resident';
import { SubscriptionBirthNonResidentFormVM } from 'shell/components/shared/research-notifications/reasearch-birth-non-resident/subscription-birth-non-resident';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ActCategory } from 'shell/models/act-category-list';
import { HttpResponse } from 'shell/models/http-response';
import { DateTime } from 'luxon';
import { CODE_STATE_ITALIAN } from 'shell/constants/code-state-italian';
import { ActType } from 'shell/constants/act-type';
import { useEventCallback } from '@mui/material';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { stateValueCitizen } from 'shell/utils/state-value-citizen';

interface ResearchBirthNonResidentNotificationsSharedProps {
  items?: BreadcrumbItem[];
  goBackUrl?: string;
  goBackUrlVisibility?: string;
}

const ResearchBirthNonResidentNotificationsShared = ({
  items,
  goBackUrl,
  goBackUrlVisibility,
}: ResearchBirthNonResidentNotificationsSharedProps): ReactElement => {
  const navigate = useNavigate();
  const params = useParams();
  const idAnsc = params.id || undefined;
  const idNotifica = params.idNotifica || undefined;

  const [dataEvent] = useHttpFetch<HttpResponse<ActCategory>, SubscriptionBirthNonResidentFormVM>(
    `${__ACT_API_URL__}/consultazione/evento`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      },
      transformer: (response) => {
        const data = response.data.payload.datiAggiuntiviSide;

        const [dataAnsc] = response.data.payload.consultazioneANSCResponse?.modelEventoDTO || [];

        const [holder] = data?.intestatariTranscod || [];

        const datiAggiuntiviSide = response.data.payload.datiAggiuntiviSide || {};

        const padre = datiAggiuntiviSide.padreTranscod;
        const madre = datiAggiuntiviSide.madreTranscod;
        // Split sul nome per 3727
        const formattedName = holder.nome ? holder.nome.split(',')[0] : '';

        return {
          birthdayDocument: {
            actType: ActType.AttoDigitale,
            digital: {
              documentDate: dataAnsc.dataformazione
                ? DateTime.fromISO(dataAnsc.dataformazione)
                : null,
              idAnsc: dataAnsc.idAnsc,
              istatCode: data?.idComuneRegistrazione || '',
              municipality: data?.idComuneRegistrazione
                ? {
                    id: data?.idComuneRegistrazione || '',
                    description: data?.nomeComuneRegistrazione || '',
                    province: data?.siglaProvinciaRegistrazione || '',
                  }
                : null,
              nationalNumber: dataAnsc.id,
              numberMunicipality: dataAnsc.numeroatto,
              year: dataAnsc.annoAtto,
            },
          },
          citizenship: {
            state: holder.idNazionalita
              ? {
                  id: holder.idNazionalita || '',
                  value: holder.nazionalita || '',
                }
              : stateValueCitizen(padre, madre),
          },
          generality: {
            fiscalCode: holder?.codiceFiscale,
            name: formattedName,
            surname: holder?.cognome,
            birthDate: holder?.dataNascita ? DateTime.fromISO(holder?.dataNascita) : null,
            anprIdentifier: holder?.idANPR,
            isForeign: holder?.idStatoNascita !== CODE_STATE_ITALIAN,
            aire: holder?.idComuneAIRE ? 'S' : 'N',
            gender: holder?.sesso,
            italian:
              holder?.idStatoNascita === CODE_STATE_ITALIAN
                ? {
                    municipality: holder?.idComuneNascita
                      ? {
                          id: holder?.idComuneNascita?.toString() || '',
                          description: holder?.nomeComuneNascita || '',
                          province: holder?.siglaProvinciaNascita || '',
                        }
                      : null,
                  }
                : {},
            foreign:
              holder?.idStatoNascita !== CODE_STATE_ITALIAN
                ? {
                    state: holder?.idStatoNascita
                      ? { id: holder?.idStatoNascita, value: holder?.nomeStatoNascita || '' }
                      : null,
                    municipality: holder?.idComuneNascita
                      ? {
                          id: holder?.idComuneNascita?.toString() || '',
                          description: holder?.nomeComuneNascita || '',
                          province: holder?.siglaProvinciaNascita || '',
                        }
                      : null,
                  }
                : {},
          },
          fatherhood: data?.padreTranscod
            ? {
                person: {
                  fiscalCode: data.padreTranscod?.codiceFiscale,
                  name: data.padreTranscod?.nome,
                  surname: data.padreTranscod?.cognome,
                },
                generality: {
                  fiscalCode: data.padreTranscod?.codiceFiscale,
                  name: data.padreTranscod?.nome,
                  surname: data.padreTranscod?.cognome,
                  birthDate: data.padreTranscod?.dataNascita
                    ? DateTime.fromISO(data.padreTranscod?.dataNascita)
                    : null,
                  isForeign: data.padreTranscod?.idStatoNascita !== CODE_STATE_ITALIAN,
                  aire: data.padreTranscod?.idComuneAIRE ? 'S' : 'N',
                  gender: data.padreTranscod?.sesso,
                  italian:
                    data.padreTranscod?.idStatoNascita === CODE_STATE_ITALIAN
                      ? {
                          municipality: data.padreTranscod?.idComuneNascita
                            ? {
                                id: data.padreTranscod?.idComuneNascita?.toString() || '',
                                description: data.padreTranscod?.nomeComuneNascita || '',
                                province: data.padreTranscod?.siglaProvinciaNascita || '',
                              }
                            : null,
                        }
                      : null,
                  foreign:
                    data.padreTranscod?.idStatoNascita !== CODE_STATE_ITALIAN
                      ? {
                          state: data.padreTranscod?.idStatoNascita
                            ? {
                                id: data.padreTranscod?.idStatoNascita?.toString() || '',
                                value: data.padreTranscod?.nomeStatoNascita || '',
                              }
                            : null,
                          municipality: data.padreTranscod?.idComuneNascita
                            ? {
                                id: data.padreTranscod?.idComuneNascita?.toString() || '',
                                description: data.padreTranscod?.nomeComuneNascita || '',
                                province: data.padreTranscod.siglaProvinciaNascita || '',
                              }
                            : null,
                        }
                      : null,
                },
              }
            : undefined,
          motherhood: data?.madreTranscod
            ? {
                person: {
                  fiscalCode: data.madreTranscod?.codiceFiscale,
                  name: data.madreTranscod?.nome,
                  surname: data.madreTranscod?.cognome,
                },
                generality: {
                  fiscalCode: data.madreTranscod?.codiceFiscale,
                  name: data.madreTranscod?.nome,
                  surname: data.madreTranscod?.cognome,
                  birthDate: data.madreTranscod?.dataNascita
                    ? DateTime.fromISO(data.madreTranscod?.dataNascita)
                    : null,
                  gender: data.madreTranscod?.sesso,
                  isForeign: data.madreTranscod?.idStatoNascita !== CODE_STATE_ITALIAN,
                  aire: data.madreTranscod?.idComuneAIRE ? 'S' : 'N',
                  italian:
                    data.madreTranscod?.idStatoNascita === CODE_STATE_ITALIAN
                      ? {
                          municipality: data.madreTranscod?.idComuneNascita
                            ? {
                                id: data.madreTranscod?.idComuneNascita?.toString() || '',
                                description: data.madreTranscod?.nomeComuneNascita || '',
                                province: data.madreTranscod.siglaProvinciaNascita || '',
                              }
                            : null,
                        }
                      : null,
                  foreign:
                    data.madreTranscod?.idStatoNascita !== CODE_STATE_ITALIAN
                      ? {
                          state: data.madreTranscod?.idStatoNascita
                            ? {
                                id: data.madreTranscod?.idStatoNascita?.toString() || '',
                                value: data.madreTranscod?.nomeStatoNascita || '',
                              }
                            : null,
                          municipality: data.madreTranscod?.idComuneNascita
                            ? {
                                id: data.madreTranscod?.idComuneNascita?.toString() || '',
                                description: data.madreTranscod?.nomeComuneNascita || '',
                                province: data.madreTranscod.siglaProvinciaNascita || '',
                              }
                            : null,
                        }
                      : null,
                },
              }
            : undefined,
        };
      },
    }
  );

  const saveData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onConfirm = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.RegistrazioneEffettuata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrlVisibility || goBackUrl || '');
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={items || []} />
      {dataEvent.data && !dataEvent.pristine && !dataEvent.isLoading && !dataEvent.hasErrors ? (
        <SubscriptionBirthNonResidentPage
          initialValues={dataEvent.data as SubscriptionBirthNonResidentFormVM}
          isWithoutBreadcrumbs
          goBackUrl={goBackUrl}
          onConfirm={onConfirm}
          disabledFromAct
        />
      ) : undefined}
    </Fragment>
  );
};

ResearchBirthNonResidentNotificationsShared.displayName =
  'Research Birth Non Resident Notifications Page component';

export default ResearchBirthNonResidentNotificationsShared;
